import React from 'react';
import { motion } from 'framer-motion';
import { Users, Briefcase, Building2, GraduationCap, FileText, HeadphonesIcon, BarChart3, ArrowRight, Palette } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Nav } from '../components/Nav';
/*
  SEO SPECIFICATION:
  Title: MindStream.ing Partner Program — Earn 40% + 20% Recurring Commission
  Meta Desc: White-label MISTR for your clients. Earn 40% on first payment plus 20% recurring every month. Become the AI operations company your market needs.
  Primary KW: AI automation affiliate program
*/
export function Partners() {
  return <div className="min-h-screen bg-off-white flex flex-col" data-id="element-1836" data-mp="1mzs3u0duw0n2">
      <Helmet data-id="element-1837" data-mp="1rxk4pjwhax3e">
        <title data-id="element-1838" data-mp="1ryd4u20xmock">Partner Program | White-label MISTR | MindStream.ing</title>
        <meta name="description" content="White-label MISTR for your clients. Earn 40% on first payment plus 20% recurring every month. Become the AI operations company your market needs." data-id="element-1839" data-mp="1wwwrk4nco45z" />
      </Helmet>
      <Nav data-id="element-1840" data-mp="vredx7j8nqkl" />
      <main className="pt-20" data-id="element-1841" data-mp="vrxog36k7col">
        {/* Hero */}
        <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 px-6 bg-hero-gradient overflow-hidden" data-id="element-1842" data-mp="qs1jo1q1fh7z">
          {/* Ambient Glow Orbs */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1843" data-mp="ltq7vlg6mwn4" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1844" data-mp="htpwk9odr1ka" />

          {/* Grid Pattern Overlay */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} data-id="element-1845" data-mp="19jb250hsdgbp" />

          <div className="inner-container relative z-10 text-center max-w-4xl mx-auto" data-id="element-1846" data-mp="1x95dteqdcyud">
            <span className="inline-flex items-center px-4 py-1.5 rounded-full border border-purple/20 bg-purple/5 text-sm font-poppins font-semibold tracking-wider uppercase text-purple mb-6" data-id="element-1847" data-mp="1sh5dhl6e03x9">
              PARTNER PROGRAM
            </span>
            <h1 className="font-poppins font-extrabold text-5xl md:text-6xl text-white leading-[1.1] mb-6" data-id="element-1848" data-mp="3rqexg2mh7e7z">
              Bring MR to Your Clients.
            </h1>
            <p className="text-xl text-steel mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-1849" data-mp="3hxhe5u9ntl8b">
              White-label MR under your brand. 40% commission + 20% recurring.
              Become the AI operations company your market needs.
            </p>
            <button className="btn-primary py-4 px-8 text-lg shadow-[0_0_40px_rgba(124,58,237,0.3)] hover:shadow-[0_0_50px_rgba(124,58,237,0.5)] transition-shadow" data-id="element-1850" data-mp="38vhvkxizy939">
              Apply to Partner Program →
            </button>
          </div>
        </section>

        {/* Gradient Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-1851" data-mp="s7dzgmhidvso" />

        {/* Commission Structure */}
        <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-1852" data-mp="2hpszi67tqil4">
          {/* Ambient Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-teal/10 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1853" data-mp="2cs10ks8rhw7f" />

          <div className="inner-container relative z-10" data-id="element-1854" data-mp="2mpp4a7ocie1q">
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-1855" data-mp="2mo4awldrp15g">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-gradient-to-br from-teal/10 to-transparent backdrop-blur-sm border border-teal/30 rounded-2xl p-8 text-center shadow-[0_0_30px_rgba(7,133,183,0.15)]" data-id="element-1856" data-mp="30fitir3rj0bz">
                <div className="text-6xl font-poppins font-black text-teal mb-2 drop-shadow-[0_0_15px_rgba(7,133,183,0.5)]" data-id="element-1857" data-mp="1b3763mzckt0i">
                  40%
                </div>
                <h3 className="text-xl font-bold mb-2" data-id="element-1858" data-mp="1hhmhl1kws1gi">First Payment</h3>
                <p className="text-steel text-sm" data-id="element-1859" data-mp="1mh8xxjw19xtj">
                  Earn a massive 40% commission on the first month of any plan
                  your client chooses.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.1
            }} className="bg-gradient-to-br from-purple/10 to-transparent backdrop-blur-sm border border-purple/30 rounded-2xl p-8 text-center shadow-[0_0_30px_rgba(124,58,237,0.15)]" data-id="element-1860" data-mp="tsthqu1619gm">
                <div className="text-6xl font-poppins font-black text-purple mb-2 drop-shadow-[0_0_15px_rgba(124,58,237,0.5)]" data-id="element-1861" data-mp="2tbg9gftjj84q">
                  20%
                </div>
                <h3 className="text-xl font-bold mb-2" data-id="element-1862" data-mp="2p7qjpn14qki4">Recurring Monthly</h3>
                <p className="text-steel text-sm" data-id="element-1863" data-mp="2k96wzkepp4op">
                  Keep earning 20% every single month for the lifetime of the
                  client's subscription.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Gradient Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-purple/30 to-transparent" data-id="element-1864" data-mp="25karnv41i5rs" />

        {/* Partner Models */}
        <section className="section-padding bg-dark-bg relative" data-id="element-1865" data-mp="21i5x8u5rp543">
          <div className="inner-container relative z-10" data-id="element-1866" data-mp="1wmguyj2d5a43">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1867" data-mp="1rlt7g7dcfbem">
              <span className="eyebrow" data-id="element-1868" data-mp="1ro6zs8gn29ih">How We Work Together</span>
              <h2 className="h2-title" data-id="element-1869" data-mp="1moaxcdnlaq51">Choose Your Partner Model</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-id="element-1870" data-mp="1cycdwz5578k2">
              {[{
              icon: <Users size={32} data-id="element-1871" data-mp="1hzi70y2psxu4" />,
              title: 'Referral Partner',
              desc: 'Simply introduce your network to MindStream.ing. We handle the sales, onboarding, and support. You collect the commission.',
              color: 'text-teal',
              bg: 'bg-teal/10'
            }, {
              icon: <Briefcase size={32} data-id="element-1872" data-mp="1vs7wjchgckvz" />,
              title: 'Reseller',
              desc: 'Sell MR directly to your clients as part of your service offering. We deliver the technology, you manage the relationship.',
              color: 'text-purple',
              bg: 'bg-purple/10'
            }, {
              icon: <Building2 size={32} data-id="element-1873" data-mp="2enoia3u56ohd" />,
              title: 'White-Label Agency',
              desc: 'Put your logo on MR. Your clients never see the MindStream.ing brand. You own the entire experience and pricing.',
              color: 'text-red-cta',
              bg: 'bg-red-cta/10'
            }].map((model, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-white/30 hover:shadow-[0_0_30px_rgba(255,255,255,0.05)] transition-all group" data-id="element-1874" data-mp="hqadnnuvfs79" data-mpi={i}>
                  <div className={`${model.bg} ${model.color} w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`} data-id="element-1875" data-mp="15fv22m9snhnx">
                    {model.icon}
                  </div>
                  <h3 className="font-poppins font-bold text-2xl mb-4" data-id="element-1876" data-mp="33y7uxdtg0uts">
                    {model.title}
                  </h3>
                  <p className="text-steel leading-relaxed" data-id="element-1877" data-mp="38uybe295tsfq">{model.desc}</p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* Gradient Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-teal/30 to-transparent" data-id="element-1878" data-mp="3mo645x8z8tz0" />

        {/* What Partners Get */}
        <section className="section-padding bg-dark-bg relative overflow-hidden" data-id="element-1879" data-mp="3rmzjlv7tqv1t">
          {/* Ambient Glow */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-purple/10 rounded-full blur-[100px] opacity-30 pointer-events-none" data-id="element-1880" data-mp="fwtl73q2ut6" />

          <div className="inner-container relative z-10" data-id="element-1881" data-mp="2282ty0z785s2">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1882" data-mp="228kz5n3u9uc4">
              <span className="eyebrow" data-id="element-1883" data-mp="1up20y9m0xuiy">Partner Resources</span>
              <h2 className="h2-title" data-id="element-1884" data-mp="1py3f6ui9o48b">Everything You Need to Win</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-id="element-1885" data-mp="1pw8tvf332a3p">
              {[{
              icon: <GraduationCap size={24} data-id="element-1886" data-mp="1g05rc84bk9g8" />,
              title: 'Training & Certification',
              desc: 'Master the platform with our comprehensive partner academy and get officially certified.'
            }, {
              icon: <FileText size={24} data-id="element-1887" data-mp="28pcp34vy8v0f" />,
              title: 'Co-Branded Materials',
              desc: 'Access pitch decks, case studies, and marketing collateral ready for your logo.'
            }, {
              icon: <HeadphonesIcon size={24} data-id="element-1888" data-mp="sa34c6f9htoh" />,
              title: 'Dedicated Partner Support',
              desc: 'Skip the queue. Get direct access to our partner success team for technical and sales support.'
            }, {
              icon: <BarChart3 size={24} data-id="element-1889" data-mp="1twps2ebxq2u3" />,
              title: 'Revenue Dashboard',
              desc: 'Track your leads, active clients, and upcoming payouts in real-time through your partner portal.'
            }].map((feature, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: i % 2 === 0 ? -20 : 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} className="flex items-start gap-4 bg-gradient-to-br from-white/[0.05] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-teal/30 hover:shadow-[0_0_20px_rgba(7,133,183,0.1)] transition-all" data-id="element-1890" data-mp="1ywu7e113wriq" data-mpi={i}>
                  <div className="text-teal shrink-0 mt-1 bg-teal/10 p-2 rounded-lg" data-id="element-1891" data-mp="ncgikdss0xp6">
                    {feature.icon}
                  </div>
                  <div data-id="element-1892" data-mp="33x1xafe5qd7k">
                    <h3 className="font-poppins font-bold text-lg mb-2" data-id="element-1893" data-mp="33vh68zr1xceg">
                      {feature.title}
                    </h3>
                    <p className="text-steel text-sm" data-id="element-1894" data-mp="2p61p8bhbolby">{feature.desc}</p>
                  </div>
                </motion.div>)}
            </div>

            <div className="mt-16 text-center" data-id="element-1895" data-mp="2g6eolrwf3jjy">
              <button className="btn-primary flex items-center gap-2 mx-auto" data-id="element-1896" data-mp="2l6artoxaj6yg">
                Apply to Partner Program <ArrowRight size={20} data-id="element-1897" data-mp="2691p5z2jegrf" />
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>;
}